import { EntityCard } from '../shared/EntityCard';
import { Badge } from '../ui/Badge';
import {
  Calendar,
  Clock
} from 'lucide-react';
import { List, LIST_STATUS_CONFIG } from '@/types/lists';
import { calendarService } from '@/services/calendarService';
import { useListsStore } from '@/store/listsStore';
import { cn } from '@/utils/cn';

interface ListCardProps {
  list: List;
  onEdit?: (list: List) => void;
  onDelete?: (list: List) => void;
  onItemClick?: (list: List) => void;
  isDragging?: boolean;
  dragHandleProps?: any;
  className?: string;
  showPriority?: boolean;
  contextDate?: Date;
}

export function ListCard({
  list,
  onEdit,
  onDelete,
  onItemClick,
  isDragging,
  dragHandleProps,
  className,
  showPriority = false,
  contextDate = new Date()
}: ListCardProps) {
  const { getListDataForContext } = useListsStore();

  const listData = getListDataForContext(list.id, contextDate);
  const contextualStatus = listData?.status || list.status;
  const statusConfig = LIST_STATUS_CONFIG[contextualStatus];

  const schedules = calendarService.getSchedulesForList(list.id);
  const hasActiveSchedule = schedules.some(s => s.isActive);

  return (
    <EntityCard
      testId="list-card"
      title={list.title}
      description={list.description}
      status={contextualStatus}
      statusLabel={statusConfig.label}
      priority={list.priority}
      showPriority={showPriority}
      progress={listData?.progress || 0}
      deadline={list.deadline}
      colorIndicator={list.color}
      className={cn('list-card', className)}
      isDragging={isDragging}
      dragHandleProps={dragHandleProps}
      onEdit={onEdit ? () => onEdit(list) : undefined}
      onDelete={onDelete ? () => onDelete(list) : undefined}
      onClick={onItemClick ? () => onItemClick(list) : undefined}
      badges={
        <>
          {hasActiveSchedule && (
            <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 border-blue-200">
              <Calendar className="h-3 w-3 mr-1" />
              Scheduled
            </Badge>
          )}
        </>
      }
      metadata={
        <>
          {/* Progress with contextual data */}
          <div className="entity-card-meta">
            {listData?.completedItems || 0}/{listData?.totalItems || list.items.length} items completed
          </div>

          {list.estimatedHours && (
            <div className="entity-card-meta">
              <Clock className="h-3 w-3" />
              <span>
                {list.estimatedHours}h estimated
                {list.actualHours && ` • ${list.actualHours}h actual`}
              </span>
            </div>
          )}
        </>
      }
    />
  );
}
